import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  LinearProgress,
  Chip,
  TextField,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Link as LinkIcon,
  Lightbulb as LightbulbIcon,
  Send as SendIcon
} from '@mui/icons-material';
import dataStrategyModulesService, { DataStrategyModule } from '../services/dataStrategyModulesService';

const DataStrategyModuleDetail: React.FC = () => {
  const navigate = useNavigate();
  const { moduleNumber } = useParams<{ moduleNumber: string }>();
  const [module, setModule] = useState<DataStrategyModule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editingReflection, setEditingReflection] = useState<number | null>(null);
  const [sectionContent, setSectionContent] = useState<string>('');
  const [reflectionResponse, setReflectionResponse] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const moduleNum = parseInt(moduleNumber || '1', 10);

  useEffect(() => {
    if (moduleNumber) {
      fetchModule();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleNumber]);

  const fetchModule = async () => {
    try {
      setLoading(true);
      console.log('Fetching module number:', moduleNum);
      const data = await dataStrategyModulesService.getModule(moduleNum);
      console.log('Module data received:', data);
      if (!data) {
        console.error('Module data is null or undefined');
        setError('Module not found in database');
      } else {
        setModule(data);
        setError(null);
      }
    } catch (err: any) {
      console.error('Error fetching module:', err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.error || 'Failed to load module. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleObjectiveToggle = async (objectiveId: string, completed: boolean) => {
    if (!module) return;
    
    try {
      const updated = await dataStrategyModulesService.updateObjective(moduleNum, objectiveId, completed);
      setModule(updated);
    } catch (err) {
      console.error('Error updating objective:', err);
    }
  };

  const handleEditSection = (sectionId: string, content: string) => {
    setEditingSection(sectionId);
    setSectionContent(content);
  };

  const handleSaveSection = async (sectionId: string) => {
    if (!module) return;
    
    try {
      setSaving(true);
      const updated = await dataStrategyModulesService.updateSection(
        moduleNum,
        sectionId,
        sectionContent,
        'Current User' // In production, get from auth context
      );
      setModule(updated);
      setEditingSection(null);
      setSectionContent('');
    } catch (err) {
      console.error('Error saving section:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingSection(null);
    setEditingReflection(null);
    setSectionContent('');
    setReflectionResponse('');
  };

  const handleEditReflection = (index: number, response: string) => {
    setEditingReflection(index);
    setReflectionResponse(response);
  };

  const handleSaveReflection = async (index: number) => {
    if (!module) return;
    
    try {
      setSaving(true);
      const updated = await dataStrategyModulesService.updateReflection(moduleNum, index, reflectionResponse);
      setModule(updated);
      setEditingReflection(null);
      setReflectionResponse('');
    } catch (err) {
      console.error('Error saving reflection:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitAssignment = async () => {
    if (!module) return;
    
    if (!window.confirm('Are you sure you want to submit this module for review? You can still edit it after submission.')) {
      return;
    }

    try {
      setSaving(true);
      const updated = await dataStrategyModulesService.submitAssignment(moduleNum);
      setModule(updated);
      alert('Module submitted successfully! Your instructor will review it soon.');
    } catch (err) {
      console.error('Error submitting assignment:', err);
      alert('Failed to submit module. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !module) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error || 'Module not found'}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/data-strategy-modules')}
          sx={{ mt: 2 }}
        >
          Back to Modules
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header with Back Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/data-strategy-modules')}
          sx={{ mr: 2 }}
          aria-label="Back to Data Strategy Modules"
        >
          Back
        </Button>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            Module {module.moduleNumber}: {module.title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {module.description}
          </Typography>
        </Box>
      </Box>

      {/* Status and Progress Card */}
      <Card sx={{ mb: 3, borderTop: '4px solid #003366' }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Box mt={1}>
                <Chip 
                  label={module.status}
                  color={
                    module.status === 'Completed' ? 'success' :
                    module.status === 'In Progress' ? 'primary' :
                    module.status === 'Under Review' ? 'warning' :
                    'default'
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="caption" color="text.secondary">
                Overall Progress
              </Typography>
              <Box mt={1}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {module.progress}%
                </Typography>
                <LinearProgress variant="determinate" value={module.progress} sx={{ mt: 1 }} />
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="caption" color="text.secondary">
                Learning Objectives
              </Typography>
              <Box mt={1}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {module.learningObjectives.filter(o => o.completed).length}/{module.learningObjectives.length}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="caption" color="text.secondary">
                Due Date
              </Typography>
              <Box mt={1}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {module.dueDate ? new Date(module.dueDate).toLocaleDateString() : 'No due date'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Learning Objectives */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold', mb: 2 }}>
            Learning Objectives
          </Typography>
          <List>
            {module.learningObjectives.map((objective) => (
              <ListItem key={objective.objectiveId} sx={{ pl: 0 }}>
                <ListItemIcon>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={objective.completed}
                        onChange={(e) => handleObjectiveToggle(objective.objectiveId, e.target.checked)}
                        icon={<RadioButtonUncheckedIcon />}
                        checkedIcon={<CheckCircleIcon />}
                      />
                    }
                    label=""
                  />
                </ListItemIcon>
                <ListItemText
                  primary={objective.description}
                  sx={{
                    textDecoration: objective.completed ? 'line-through' : 'none',
                    opacity: objective.completed ? 0.7 : 1
                  }}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Content Sections */}
      <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold', mb: 2 }}>
        Module Content
      </Typography>
      {module.sections.sort((a, b) => a.order - b.order).map((section) => (
        <Accordion key={section.sectionId} defaultExpanded={section.order === 1}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold' }}>{section.title}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {editingSection === section.sectionId ? (
              <Box>
                <TextField
                  fullWidth
                  multiline
                  rows={10}
                  value={sectionContent}
                  onChange={(e) => setSectionContent(e.target.value)}
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={() => handleSaveSection(section.sectionId)}
                    disabled={saving}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancelEdit}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box
                onClick={() => section.isEditable && handleEditSection(section.sectionId, section.content)}
                onKeyDown={(e) => {
                  if (section.isEditable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleEditSection(section.sectionId, section.content);
                  }
                }}
                role={section.isEditable ? "button" : undefined}
                tabIndex={section.isEditable ? 0 : undefined}
                aria-label={section.isEditable ? `Click to edit ${section.title}` : undefined}
                sx={{
                  cursor: section.isEditable ? 'pointer' : 'default',
                  p: 2,
                  borderRadius: 1,
                  transition: 'all 0.2s ease-in-out',
                  position: 'relative',
                  '&:hover': section.isEditable ? {
                    backgroundColor: 'rgba(0, 51, 102, 0.04)',
                    boxShadow: '0 2px 8px rgba(0, 51, 102, 0.1)',
                  } : {},
                  '&:focus': section.isEditable ? {
                    outline: '3px solid #005fcc',
                    outlineOffset: '2px',
                  } : {},
                }}
              >
                {section.isEditable && (
                  <EditIcon 
                    sx={{ 
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      color: '#003366',
                      fontSize: '1.2rem',
                      opacity: 0.6,
                    }} 
                  />
                )}
                <Box sx={{ mb: 2, pr: section.isEditable ? 4 : 0 }}>
                  <ReactMarkdown
                    components={{
                      p: ({children}) => <Typography variant="body1" paragraph>{children}</Typography>,
                      strong: ({children}) => <strong style={{ fontWeight: 700, color: '#003366' }}>{children}</strong>,
                      em: ({children}) => <em style={{ fontStyle: 'italic', color: '#555' }}>{children}</em>,
                      h1: ({children}) => <Typography variant="h4" sx={{ mt: 3, mb: 2, color: '#003366', fontWeight: 'bold' }}>{children}</Typography>,
                      h2: ({children}) => <Typography variant="h5" sx={{ mt: 2, mb: 1, color: '#003366', fontWeight: 'bold' }}>{children}</Typography>,
                      h3: ({children}) => <Typography variant="h6" sx={{ mt: 2, mb: 1, color: '#003366', fontWeight: 'bold' }}>{children}</Typography>,
                      ul: ({children}) => <Box component="ul" sx={{ pl: 3, mb: 2 }}>{children}</Box>,
                      ol: ({children}) => <Box component="ol" sx={{ pl: 3, mb: 2 }}>{children}</Box>,
                      li: ({children}) => <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>{children}</Typography>,
                      hr: () => <Box component="hr" sx={{ my: 3, border: 'none', borderTop: '2px solid #e0e0e0' }} />
                    }}
                  >
                    {section.content}
                  </ReactMarkdown>
                </Box>
                {section.lastEditedAt && (
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    Last edited: {new Date(section.lastEditedAt).toLocaleString()}
                  </Typography>
                )}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Key Takeaways */}
      {module.keyTakeaways && module.keyTakeaways.length > 0 && (
        <Card sx={{ mt: 3, mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <LightbulbIcon sx={{ color: '#ff9800', mr: 1 }} />
              <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold' }}>
                Key Takeaways
              </Typography>
            </Box>
            <List>
              {module.keyTakeaways.map((takeaway, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: '#4caf50' }} />
                  </ListItemIcon>
                  <ListItemText primary={takeaway} />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Reflections */}
      {module.reflections && module.reflections.length > 0 && (
        <>
          <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold', mb: 2 }}>
            Reflection Questions
          </Typography>
          {module.reflections.map((reflection, index) => (
            <Card key={index} sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Question {index + 1}: {reflection.question}
                </Typography>
                {editingReflection === index ? (
                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={6}
                      value={reflectionResponse}
                      onChange={(e) => setReflectionResponse(e.target.value)}
                      variant="outlined"
                      placeholder="Type your reflection here..."
                      sx={{ mb: 2 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                      Word count: {reflectionResponse.trim().split(/\s+/).filter(w => w.length > 0).length}
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={() => handleSaveReflection(index)}
                        disabled={saving}
                      >
                        Save Response
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={handleCancelEdit}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    {reflection.response ? (
                      <Box
                        onClick={() => handleEditReflection(index, reflection.response)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleEditReflection(index, reflection.response);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={`Click to edit response for question ${index + 1}`}
                        sx={{
                          cursor: 'pointer',
                          p: 2,
                          borderRadius: 1,
                          transition: 'all 0.2s ease-in-out',
                          position: 'relative',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 51, 102, 0.04)',
                            boxShadow: '0 2px 8px rgba(0, 51, 102, 0.1)',
                          },
                          '&:focus': {
                            outline: '3px solid #005fcc',
                            outlineOffset: '2px',
                          },
                        }}
                      >
                        <EditIcon 
                          sx={{ 
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            color: '#003366',
                            fontSize: '1.2rem',
                            opacity: 0.6,
                          }} 
                        />
                        <Box sx={{ mb: 2, pr: 4 }}>
                          <ReactMarkdown
                            components={{
                              p: ({children}) => <Typography variant="body1" paragraph>{children}</Typography>,
                              strong: ({children}) => <strong style={{ fontWeight: 700, color: '#003366' }}>{children}</strong>,
                              em: ({children}) => <em style={{ fontStyle: 'italic', color: '#555' }}>{children}</em>,
                              ul: ({children}) => <Box component="ul" sx={{ pl: 3, mb: 2 }}>{children}</Box>,
                              ol: ({children}) => <Box component="ol" sx={{ pl: 3, mb: 2 }}>{children}</Box>,
                              li: ({children}) => <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>{children}</Typography>
                            }}
                          >
                            {reflection.response}
                          </ReactMarkdown>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Word count: {reflection.wordCount}
                          {reflection.lastEditedAt && ` • Last edited: ${new Date(reflection.lastEditedAt).toLocaleString()}`}
                        </Typography>
                      </Box>
                    ) : (
                      <Button
                        variant="outlined"
                        onClick={() => handleEditReflection(index, '')}
                        fullWidth
                        sx={{ 
                          py: 2,
                          borderStyle: 'dashed',
                          '&:hover': {
                            borderStyle: 'solid',
                          }
                        }}
                      >
                        Add Your Response
                      </Button>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </>
      )}

      {/* Resources */}
      {module.resources && module.resources.length > 0 && (
        <Card sx={{ mt: 3, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold', mb: 2 }}>
              Additional Resources
            </Typography>
            <List>
              {module.resources.map((resource, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <LinkIcon sx={{ color: '#2196f3' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={resource.title}
                    secondary={
                      <Box component="span" display="flex" alignItems="center" gap={1}>
                        <Chip label={resource.type} size="small" />
                        <Typography
                          component="a"
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: '#2196f3', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          Open Resource →
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Submit Assignment */}
      <Card sx={{ mb: 3, borderTop: module.assignmentSubmitted ? '4px solid #4caf50' : '4px solid #ff9800' }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                {module.assignmentSubmitted ? 'Assignment Submitted' : 'Ready to Submit?'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {module.assignmentSubmitted
                  ? `Submitted on ${module.assignmentSubmittedAt ? new Date(module.assignmentSubmittedAt).toLocaleString() : 'Unknown'}`
                  : 'Submit your module for instructor review when you\'re ready'}
              </Typography>
              {module.grade !== 'Not Graded' && (
                <Chip label={`Grade: ${module.grade}`} color="success" sx={{ mt: 1 }} />
              )}
            </Box>
            <Button
              variant="contained"
              size="large"
              startIcon={<SendIcon />}
              onClick={handleSubmitAssignment}
              disabled={saving || module.assignmentSubmitted}
              sx={{
                backgroundColor: '#003366',
                '&:hover': {
                  backgroundColor: '#002244'
                }
              }}
            >
              {module.assignmentSubmitted ? 'Submitted' : 'Submit Module'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default DataStrategyModuleDetail;
